/**
 * Invitations Page for Regular Users
 *
 * Allows users to send group invitations and manage pending invitations.
 */

import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { useNotification } from '../../components/ui/NotificationToast';

interface Group {
  id: string;
  name: string;
  description: string | null;
  role: 'owner' | 'admin' | 'member';
  memberCount: number;
}

interface Invitation {
  id: string;
  type: 'platform' | 'group';
  email: string;
  groupId: string | null;
  groupName: string | null;
  role: string;
  invitedBy: string;
  inviterEmail: string;
  expiresAt: string;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface NewInvitation {
  id: string;
  token: string;
  email: string;
  type: string;
  groupId?: string;
  groupName?: string;
  role: string;
  expiresAt: string;
}

export default function InvitationsPage() {
  const { showNotification } = useNotification();
  const [groups, setGroups] = useState<Group[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Send invitation modal
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({
    email: '',
    groupId: '',
    role: 'member' as 'admin' | 'member',
    message: '',
  });
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Success modal with invite link
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newInvitation, setNewInvitation] = useState<NewInvitation | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, [pagination.page]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load groups where user is owner or admin
      const groupsResponse = await api.get('/api/groups');
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json() as { groups: Group[] };
        // Filter to only groups where user can invite (owner or admin)
        const invitableGroups = groupsData.groups.filter(
          (g) => g.role === 'owner' || g.role === 'admin'
        );
        setGroups(invitableGroups);

        // Set default group if available
        if (invitableGroups.length > 0 && !sendForm.groupId) {
          setSendForm((f) => ({ ...f, groupId: invitableGroups[0].id }));
        }
      }

      // Load invitations
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        type: 'group',
      });

      const response = await api.get(`/api/invitations?${params}`);
      if (response.ok) {
        const data = await response.json() as { invitations: Invitation[]; pagination: Pagination };
        setInvitations(data.invitations);
        setPagination(data.pagination);
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to load invitations');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendLoading(true);
    setSendError(null);

    if (!sendForm.groupId) {
      setSendError('Please select a group');
      setSendLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/invitations', {
        email: sendForm.email,
        type: 'group',
        groupId: sendForm.groupId,
        role: sendForm.role,
        message: sendForm.message || undefined,
      });

      if (response.ok) {
        const data = await response.json() as { invitation: NewInvitation };
        setNewInvitation(data.invitation);
        setShowSendModal(false);
        setShowSuccessModal(true);
        setSendForm((f) => ({ ...f, email: '', message: '', role: 'member' }));
        loadData();
      } else {
        const data = await response.json() as { error?: string };
        setSendError(data.error || 'Failed to send invitation');
      }
    } catch {
      setSendError('Failed to connect to server');
    } finally {
      setSendLoading(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      const response = await api.delete(`/api/invitations/${invitationId}`);
      if (response.ok) {
        showNotification('success', 'Invitation revoked successfully');
        loadData();
      } else {
        const data = await response.json() as { error?: string };
        showNotification('error', data.error || 'Failed to revoke invitation');
      }
    } catch {
      showNotification('error', 'Failed to connect to server');
    }
  };

  const getInviteLink = (token: string) => {
    return `${window.location.origin}/invite/${token}`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      showNotification('success', 'Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      showNotification('success', 'Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const canInvite = groups.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invitations</h1>
          <p className="text-gray-600 mt-1">Invite members to your groups</p>
        </div>
        <button
          onClick={() => setShowSendModal(true)}
          disabled={!canInvite}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          title={!canInvite ? 'You need to own or be an admin of a group to send invitations' : ''}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Send Invitation
        </button>
      </div>

      {/* No Groups Message */}
      {!loading && groups.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">No groups available</h3>
              <p className="text-sm text-yellow-700 mt-1">
                You need to create a group or be an admin of a group to send invitations.
                Create a group first to start inviting members.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Invitations Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pending invitations</h3>
            <p className="mt-1 text-sm text-gray-500">
              {canInvite ? 'Send an invitation to get started.' : 'Create a group first to send invitations.'}
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invitations.map((invitation) => (
                <tr key={invitation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invitation.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invitation.groupName || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(invitation.role)}`}>
                      {invitation.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invitation.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {isExpired(invitation.expiresAt) ? (
                      <span className="text-red-600">Expired</span>
                    ) : (
                      <span className="text-gray-500">
                        {new Date(invitation.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => handleRevokeInvitation(invitation.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} invitations
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Send Invitation Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Send Invitation</h2>

            {sendError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {sendError}
              </div>
            )}

            <form onSubmit={handleSendInvitation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group
                </label>
                <select
                  value={sendForm.groupId}
                  onChange={(e) => setSendForm(f => ({ ...f, groupId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.memberCount} members)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={sendForm.email}
                  onChange={(e) => setSendForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={sendForm.role}
                  onChange={(e) => setSendForm(f => ({ ...f, role: e.target.value as 'member' | 'admin' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Members can view group content. Admins can also manage the group and invite others.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personal Message <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={sendForm.message}
                  onChange={(e) => setSendForm(f => ({ ...f, message: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add a personal note to the invitation..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowSendModal(false);
                    setSendError(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {sendLoading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Modal with Invite Link */}
      {showSuccessModal && newInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Invitation Sent!</h2>
              <p className="text-gray-600 mt-1">
                Share this link with <strong>{newInvitation.email}</strong>
              </p>
              {newInvitation.groupName && (
                <p className="text-sm text-gray-500 mt-1">
                  To join <strong>{newInvitation.groupName}</strong>
                </p>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invitation Link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={getInviteLink(newInvitation.token)}
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                />
                <button
                  onClick={() => copyToClipboard(getInviteLink(newInvitation.token))}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                This link expires on {new Date(newInvitation.expiresAt).toLocaleDateString()}
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> This is the only time the invitation link will be shown.
                Make sure to copy it now or send it directly to the recipient.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setNewInvitation(null);
                  setCopied(false);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
